import { ContentError } from './errors'
import type { BundleAsset, BundleConcept, BundleStats, ContentBundle } from './types'

const HASH = /^sha256:[0-9a-f]{64}$/

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

function safeRelativePath(value: string): boolean {
  if (value.startsWith('/') || value.startsWith('\\')) return false
  return value.split(/[\\/]/).every((part) => part !== '..' && part !== '')
}

function readStats(value: unknown): BundleStats | undefined {
  if (value === undefined) return undefined
  if (!isRecord(value)) throw new ContentError('invalid', 'content.json 的 stats 不是对象')

  const { concepts, questions, cards } = value
  if (
    typeof concepts !== 'number' ||
    typeof questions !== 'number' ||
    typeof cards !== 'number' ||
    !Number.isInteger(concepts) ||
    !Number.isInteger(questions) ||
    !Number.isInteger(cards)
  ) {
    throw new ContentError('invalid', 'content.json 的 stats 三个值必须是整数')
  }
  return { concepts, questions, cards }
}

function readConcept(value: unknown, index: number): BundleConcept {
  if (!isRecord(value)) throw new ContentError('invalid', `concepts[${index}] 不是对象`)
  const { path, text } = value
  if (typeof path !== 'string' || !safeRelativePath(path) || !path.startsWith('concepts/') || !path.endsWith('.md')) {
    throw new ContentError('invalid', `concepts[${index}].path 不是 concepts/ 下的相对 md 路径`)
  }
  if (typeof text !== 'string' || text.length === 0) {
    throw new ContentError('invalid', `concepts[${index}].text 是空内容`)
  }
  return { path, text }
}

function readAsset(value: unknown, index: number): BundleAsset {
  if (!isRecord(value)) throw new ContentError('invalid', `assets[${index}] 不是对象`)
  const { path, size, sha256 } = value
  if (typeof path !== 'string' || !safeRelativePath(path)) {
    throw new ContentError('invalid', `assets[${index}].path 不是安全的相对路径`)
  }
  if (typeof size !== 'number' || !Number.isInteger(size) || size < 0) {
    throw new ContentError('invalid', `assets[${index}].size 不是非负整数`)
  }
  if (typeof sha256 !== 'string' || !/^[0-9a-f]{64}$/.test(sha256)) {
    throw new ContentError('invalid', `assets[${index}].sha256 不是 sha256`)
  }
  return { path, size, sha256 }
}

/**
 * 只校验客户端真正依赖的字段：
 * schemaVersion、contentHash、concepts[].path/text、assets 的形状。
 * stats 是展示用，缺了不拦；路径不允许越出仓库。
 */
export function parseBundle(input: unknown): ContentBundle {
  if (!isRecord(input)) throw new ContentError('invalid', 'content.json 顶层不是对象')

  if (input.schemaVersion !== 1) {
    throw new ContentError('invalid', `不支持的 schemaVersion: ${String(input.schemaVersion)}`)
  }

  if (typeof input.contentHash !== 'string' || !HASH.test(input.contentHash)) {
    throw new ContentError('invalid', 'contentHash 不是 sha256:<64 hex>')
  }

  if (!Array.isArray(input.concepts) || input.concepts.length === 0) {
    throw new ContentError('invalid', 'concepts 必须是非空数组')
  }

  const concepts = input.concepts.map(readConcept)
  const seen = new Set<string>()
  for (const concept of concepts) {
    if (seen.has(concept.path)) throw new ContentError('invalid', `概念路径重复: ${concept.path}`)
    seen.add(concept.path)
  }

  if (input.assets !== undefined && !Array.isArray(input.assets)) {
    throw new ContentError('invalid', 'assets 必须是数组')
  }
  const assetValues: unknown[] = Array.isArray(input.assets) ? input.assets : []
  const assets = assetValues.map(readAsset)
  const assetSeen = new Set<string>()
  for (const asset of assets) {
    if (assetSeen.has(asset.path)) throw new ContentError('invalid', `资产路径重复: ${asset.path}`)
    assetSeen.add(asset.path)
  }

  return {
    schemaVersion: input.schemaVersion,
    contentHash: input.contentHash,
    stats: readStats(input.stats),
    concepts,
    assets,
  }
}
